import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from './AuthContext';

// CloudContext holds cross-cutting UI state: the provider list and the
// currently scoped provider. The app is light-mode only.
const CloudContext = createContext(null);

export function CloudProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [providers, setProviders] = useState([]);
  const [activeProvider, setActiveProvider] = useState(''); // set to first provider once loaded

  // Providers require authentication — (re)load once the user signs in, and
  // clear the list on logout so nothing stale lingers.
  useEffect(() => {
    if (!isAuthenticated) {
      setProviders([]);
      return;
    }
    api
      .getProviders()
      .then((ps) => {
        setProviders(ps);
        // A provider must always be selected — default to the first one.
        setActiveProvider((cur) => cur || ps[0]?.id || '');
      })
      .catch(() => setProviders([]));
  }, [isAuthenticated]);

  // Light mode only.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  const value = useMemo(
    () => ({
      providers,
      activeProvider,
      setActiveProvider,
    }),
    [providers, activeProvider]
  );

  return <CloudContext.Provider value={value}>{children}</CloudContext.Provider>;
}

export function useCloud() {
  const ctx = useContext(CloudContext);
  if (!ctx) throw new Error('useCloud must be used within a CloudProvider');
  return ctx;
}
